import { forwardRef, useEffect, useRef, useState, type ReactNode } from "react";
import { Input } from "@marekl/adminer/components";
import { decimalText, parseDecimal } from "../../lib/money";

export interface DecimalInputProps {
  /** Integer in units of 10^-scale (minor units at 2, thousandths at 3). */
  value: number | null;
  onChange: (value: number | null) => void;
  scale: number;
  label?: string;
  error?: string;
  hint?: string;
  suffix?: ReactNode;
  required?: boolean;
  disabled?: boolean;
  allowNegative?: boolean;
  name?: string;
  id?: string;
  "aria-label"?: string;
  onBlur?: () => void;
  className?: string;
}

/**
 * Exact decimal entry (ADMINER_PROPOSALS A1, app-local until the library
 * has it): the text the user types is parsed by string arithmetic into an
 * integer — 12,50 Kč is 1250, 1,5 ks is 1500 — never through a float. Text
 * that is not a number keeps the last valid value and says so. Outside
 * typing it shows every decimal ("12,50", "1,500") with a "0,00"
 * placeholder, like the legacy DecimalNumberInput.
 */
export const DecimalInput = forwardRef<HTMLInputElement, DecimalInputProps>(function DecimalInput(
  { value, onChange, scale, error, allowNegative = false, onBlur, ...rest },
  ref,
) {
  const [text, setText] = useState(() => decimalText(value, scale));
  const [invalid, setInvalid] = useState(false);
  // The value this field last emitted: a different incoming value came from
  // outside (a product picked, a reset) and replaces the text.
  const emitted = useRef<number | null>(value);

  useEffect(() => {
    if (value !== emitted.current) {
      emitted.current = value;
      setText(decimalText(value, scale));
      setInvalid(false);
    }
  }, [value, scale]);

  function change(next: string) {
    setText(next);
    const parsed = parseDecimal(next, scale);
    if (parsed === undefined || (!allowNegative && parsed !== null && parsed < 0)) {
      setInvalid(true);
      return;
    }
    setInvalid(false);
    emitted.current = parsed;
    onChange(parsed);
  }

  return (
    <Input
      ref={ref}
      {...rest}
      inputMode="decimal"
      placeholder={scale > 0 ? `0,${"0".repeat(scale)}` : "0"}
      autoComplete="off"
      value={text}
      onChange={(e) => change(e.target.value)}
      onFocus={(e) => e.target.select()}
      onBlur={() => {
        if (!invalid) setText(decimalText(emitted.current, scale));
        onBlur?.();
      }}
      error={
        invalid
          ? scale > 0
            ? `Zadejte číslo s nejvýše ${scale} desetinnými místy.`
            : "Zadejte celé číslo."
          : error
      }
    />
  );
});
